import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { fmtDateTime } from "../../lib/format";
import { HC } from "../hr/theme";
import { Card, SectionHead, Btn, Table, Empty } from "../hr/ui";
import { Spinner } from "./mui";
import { Modal } from "./TasksChecklists";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ManagementIntelligence() {
  const [rows, setRows] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/management/intelligence/audit").then(setRows).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const open = async (r) => {
    try { setDetail(await api.get(`/management/intelligence/audit/${r.id}`)); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!rows) return <Spinner />;

  return (
    <div className="space-y-5" data-testid="mgmt-intelligence-page">
      <Card testid="ai-governance-card">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: HC.greenTint }}>
            <ShieldCheck size={19} style={{ color: HC.green }} />
          </span>
          <div>
            <div className="font-brand font-bold text-base md:text-lg" style={{ color: HC.text }}>AI governance</div>
            <div className="text-sm mt-1" style={{ color: HC.muted }}>
              Insights are composed from a pre-computed deterministic payload. The model never queries the database,
              never performs arithmetic, and cannot act on the business. Every generation is stored with its exact
              input snapshot for audit.
            </div>
          </div>
        </div>
      </Card>

      <Card testid="ai-audit-card">
        <SectionHead title="Insight Audit Trail" sub={`${rows.length} generation(s)`} />
        <Table testid="ai-audit-table" onRowClick={open} columns={[
          { key: "generated_at", label: "Generated", render: (r) => fmtDateTime(r.generated_at) },
          { key: "scope", label: "Scope" },
          { key: "period", label: "Period" },
          { key: "model", label: "Model" },
          { key: "source", label: "Source" },
          { key: "status", label: "Status" },
          { key: "prompt_version", label: "Prompt" },
          { key: "cost", label: "Cost (USD)", align: "right", render: (r) => r.usage?.estimated_cost_usd ?? "—" },
        ]} rows={rows} empty="No AI insights generated yet" />
      </Card>

      {detail && (
        <Modal title={`Insight ${detail.id.slice(0, 8)} · ${detail.model}`} onClose={() => setDetail(null)} testid="ai-audit-modal">
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] mb-1" style={{ color: HC.soft }}>Output</div>
          <pre className="p-3 rounded-xl overflow-auto text-[11px] max-h-[35vh]" style={{ background: HC.panelSoft, color: HC.text }}>
            {JSON.stringify(detail.output, null, 2)}
          </pre>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] mb-1 mt-3" style={{ color: HC.soft }}>Input snapshot (deterministic payload)</div>
          <pre className="p-3 rounded-xl overflow-auto text-[11px] max-h-[35vh]" style={{ background: HC.panelSoft, color: HC.text }}>
            {JSON.stringify(detail.input_snapshot, null, 2)}
          </pre>
        </Modal>
      )}
    </div>
  );
}
